import React, { useState, useEffect } from 'react';
import { Search, Filter, MoreVertical, Check, X, Trash2, Eye } from 'lucide-react';
import { getBookings, updateBooking } from '../../services/api';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Appointments = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const fetchBookings = async () => {
    try {
      const data = await getBookings();
      setBookings(data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      await updateBooking(bookingId, { status: newStatus });
      setBookings(prev => prev.map(b => 
        b.id === bookingId ? { ...b, status: newStatus } : b
      ));
      toast.success(`Booking ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/bookings/${bookingId}`);
      setBookings(prev => prev.filter(b => b.id !== bookingId));
      toast.success('Booking deleted');
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const filteredBookings = bookings
    .filter(b => filter === 'all' || b.status === filter)
    .filter(b => 
      b.client_name.toLowerCase().includes(search.toLowerCase()) ||
      b.client_email.toLowerCase().includes(search.toLowerCase()) ||
      b.service_name.toLowerCase().includes(search.toLowerCase())
    );

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 skeleton rounded-lg" />
        <div className="h-12 skeleton rounded-xl" />
        {[1,2,3,4].map(i => <div key={i} className="h-20 skeleton rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="heading-1 mb-1">Appointments</h1>
        <p className="body-small text-[var(--text-secondary)]">{bookings.length} total bookings</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-light)]" />
          <input
            type="text"
            placeholder="Search by name, email, or service..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none transition-colors"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto hide-scrollbar">
          {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                filter === status
                  ? 'bg-[var(--text-primary)] text-white'
                  : 'bg-white text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Bookings List */}
      {filteredBookings.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <p className="text-[var(--text-secondary)]">No bookings found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredBookings.map((booking) => (
            <div key={booking.id} className="bg-white p-4 rounded-xl">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-[var(--text-heading)] truncate">{booking.client_name}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${getStatusColor(booking.status)}`}>
                      {booking.status}
                    </span>
                  </div>
                  <p className="body-small text-[var(--text-secondary)] truncate">{booking.service_name} - ${booking.service_price}</p>
                  <p className="body-small text-[var(--text-light)]">
                    {new Date(booking.booking_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at {booking.booking_time}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setSelectedBooking(booking); setShowDetails(true); }}
                    className="p-2 rounded-lg hover:bg-[var(--bg-subtle)] transition-colors text-[var(--text-secondary)]"
                    title="View Details"
                  >
                    <Eye size={18} />
                  </button>
                  {booking.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleStatusUpdate(booking.id, 'confirmed')}
                        className="p-2 rounded-lg hover:bg-green-50 transition-colors text-green-600"
                        title="Confirm"
                      >
                        <Check size={18} />
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(booking.id, 'cancelled')}
                        className="p-2 rounded-lg hover:bg-red-50 transition-colors text-red-500"
                        title="Cancel"
                      >
                        <X size={18} />
                      </button>
                    </>
                  )}
                  {booking.status === 'confirmed' && (
                    <button
                      onClick={() => handleStatusUpdate(booking.id, 'completed')}
                      className="p-2 rounded-lg hover:bg-green-50 transition-colors text-green-600"
                      title="Mark Completed"
                    >
                      <Check size={18} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(booking.id)}
                    className="p-2 rounded-lg hover:bg-red-50 transition-colors text-red-500"
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Details Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-md bg-white rounded-2xl">
          <DialogTitle className="heading-2 mb-4">Booking Details</DialogTitle>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="p-4 bg-[var(--bg-subtle)] rounded-xl space-y-2">
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Client</span>
                  <span className="font-medium">{selectedBooking.client_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Email</span>
                  <span>{selectedBooking.client_email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Phone</span>
                  <span>{selectedBooking.client_phone}</span>
                </div>
              </div>
              <div className="p-4 bg-[var(--bg-subtle)] rounded-xl space-y-2">
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Service</span>
                  <span className="font-medium">{selectedBooking.service_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Price</span>
                  <span>${selectedBooking.service_price}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Duration</span>
                  <span>{selectedBooking.service_duration}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Date</span>
                  <span>{new Date(selectedBooking.booking_date).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Time</span>
                  <span>{selectedBooking.booking_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Status</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${getStatusColor(selectedBooking.status)}`}>
                    {selectedBooking.status}
                  </span>
                </div>
              </div>
              {selectedBooking.notes && (
                <div className="p-4 bg-[var(--bg-subtle)] rounded-xl">
                  <p className="text-[var(--text-secondary)] text-sm mb-1">Notes</p>
                  <p>{selectedBooking.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Appointments;
